/* eslint-disable no-magic-numbers */
import { imageMaxBytes } from "../utils";
import { GIGA, MEGA } from "utils/common";
import {
	conditionalRequiredNotNull,
	conditionalValidator,
	errorMessageReducer,
	makeDynamicConfig,
	requiredAsset,
	validateUnlessExternalRender
} from "./validators";
import { filesize } from "widgets/forms/validators/Assets";
import { isProtocolOptionalURL, isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { mergeLists, uniqueVariableName } from "./utils";
import { SOCIAL_AD_TYPES } from "utils/constants";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";

export const REDDIT_IMAGE_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 300,
	MAX_LENGTH_DISCLAIMER_TEXT: 32000,
	MAX_LENGTH_LANDING_PAGE: 268,
	MAX_LENGTH_DISPLAY_LINK: 100,
	MAX_IMAGE_BYTES: imageMaxBytes(3 * MEGA),
	IMAGE_ACCEPT: ".jpeg, .jpg, .png, .gif",
	MAX_THUMBNAIL_BYTES: imageMaxBytes(3 * MEGA),
	THUMBNAIL_ACCEPT: ".jpeg, .jpg, .png, .gif",
};

export const REDDIT_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 300,
	MAX_LENGTH_DISCLAIMER_TEXT: 32000,
	MAX_LENGTH_LANDING_PAGE: 268,
	MAX_LENGTH_DISPLAY_LINK: 100,
	MIN_VIDEO_SEC: 2,
	MAX_VIDEO_MIN: 15,
	VIDEO_ACCEPT: ".mp4, .mov",
	THUMBNAIL_ACCEPT: ".jpeg, .jpg, .png, .gif",
	MAX_THUMBNAIL_BYTES: imageMaxBytes(3 * MEGA),
	MAX_VIDEO_BYTES: GIGA,
};

export const REDDIT_IMAGE_VALIDATORS = {
	image: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: REDDIT_IMAGE_CONSTRAINTS.MAX_IMAGE_BYTES,
		}),
	]),
	thumbnail: validateUnlessExternalRender([
		filesize({
			max: REDDIT_IMAGE_CONSTRAINTS.MAX_THUMBNAIL_BYTES,
		}),
	]),
	headline: [
		maxLength(REDDIT_IMAGE_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(REDDIT_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	displayLink: [
		isProtocolOptionalURL,
		maxLength(REDDIT_IMAGE_CONSTRAINTS.MAX_LENGTH_DISPLAY_LINK),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		conditionalValidator("disclaimerEnabled", maxLength(REDDIT_IMAGE_CONSTRAINTS.MAX_LENGTH_DISCLAIMER_TEXT)),
	],
};

export const REDDIT_VIDEO_VALIDATORS = {
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: REDDIT_VIDEO_CONSTRAINTS.MAX_VIDEO_BYTES,
		}),
	]),
	thumbnail: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: REDDIT_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_BYTES,
		}),
	]),
	headline: [
		maxLength(REDDIT_VIDEO_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(REDDIT_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	displayLink: [
		isProtocolOptionalURL,
		maxLength(REDDIT_VIDEO_CONSTRAINTS.MAX_LENGTH_DISPLAY_LINK),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(REDDIT_VIDEO_CONSTRAINTS.MAX_LENGTH_DISCLAIMER_TEXT),
	],
};

const REDDIT_IMAGE_WARNINGS = mergeLists(
	{
		image: [uniqueVariableName],
		thumbnail: [uniqueVariableName],
		headline: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		displayLink: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(REDDIT_IMAGE_VALIDATORS)
);

const REDDIT_VIDEO_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		thumbnail: [uniqueVariableName],
		headline: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		displayLink: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(REDDIT_VIDEO_VALIDATORS)
);

export function redditImageWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.REDDIT_IMAGE;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(REDDIT_IMAGE_WARNINGS, values, formName);
	}
}

export function redditVideoWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.REDDIT_VIDEO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(REDDIT_VIDEO_WARNINGS, values, formName);
	}
}

export function redditImageValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.REDDIT_IMAGE;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...REDDIT_IMAGE_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

export function redditVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.REDDIT_VIDEO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...REDDIT_VIDEO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
